'use client'

import React from 'react'

/* All marks are served from /public/logos. They used to be hotlinked from
   upload.wikimedia.org, which set a third-party cookie (failing two Lighthouse
   best-practices audits by itself), shipped a 508 KB Matlab PNG on every
   homepage view, and left the page's logo strip at the mercy of someone
   else's CDN. The width/height pairs are each file's intrinsic pixels so the
   browser can reserve space before the image arrives. */
const LOGOS: {
  src: string
  alt: string
  width: number
  height: number
  small?: boolean
}[] = [
  { src: '/logos/aws.svg', alt: 'AWS', width: 304, height: 182 },
  {
    src: '/logos/google-cloud.svg',
    alt: 'Google Cloud',
    width: 512,
    height: 512,
  },
  { src: '/logos/matlab-mark.webp', alt: 'MATLAB', width: 179, height: 160 },
  {
    src: '/logos/google-drive.svg',
    alt: 'Google Drive',
    width: 87,
    height: 78,
  },
  {
    src: '/logos/google-sheets.svg',
    alt: 'Google Sheets',
    width: 64,
    height: 88,
  },
  {
    src: '/logos/google-docs.svg',
    alt: 'Google Docs',
    width: 47,
    height: 65,
  },
  {
    src: '/images/vocareum_logo_dark.webp',
    alt: 'Vocareum',
    width: 300,
    height: 60,
    small: true,
  },
]

/* Straight hairline rail — the same visual language as the trusted-teams
   strip: cells separated by 1px dividers between top/bottom hairlines,
   auto-scrolling marquee, no skew and no scroll-reactive angle. */
export default function LogoMarquee() {
  return (
    <section aria-label="Partner logos" className="w-full bg-transparent">
      <div className="w-full py-8 md:py-12">
        <div className="w-full overflow-hidden border-y border-border/70">
          <ul className="animate-marquee-left flex w-max items-stretch">
            {/* Original + clone for seamless loop */}
            {[...LOGOS, ...LOGOS].map((logo, i) => (
              <li
                key={i}
                aria-hidden={i >= LOGOS.length}
                className="logo-tile shrink-0 border-r border-border/70"
              >
                <img
                  src={logo.src}
                  alt={i < LOGOS.length ? logo.alt : ''}
                  width={logo.width}
                  height={logo.height}
                  loading="lazy"
                  decoding="async"
                  className={logo.small ? 'logo-img logo-img--sm' : 'logo-img'}
                />
                <span className="logo-name">{logo.alt}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <style jsx>{`
        .logo-tile {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-width: 160px;
          height: 112px;
          padding: 12px 16px;
        }

        .logo-img {
          max-height: 22px;
          width: auto;
          object-fit: contain;
          display: block;
        }

        /* Vocareum's wordmark reads visually larger than the others, so it
           gets a reduced cap height at every breakpoint. */
        .logo-img--sm {
          max-height: 15px;
        }

        .logo-name {
          font-size: 10px;
          font-weight: 500;
          color: hsl(var(--muted-foreground));
          letter-spacing: 0.04em;
          white-space: nowrap;
        }

        @media (min-width: 640px) {
          .logo-tile {
            min-width: 220px;
            height: 128px;
            gap: 10px;
          }
          .logo-img {
            max-height: 26px;
          }
          .logo-img--sm {
            max-height: 18px;
          }
          .logo-name {
            font-size: 11px;
          }
        }

        @media (min-width: 768px) {
          .logo-tile {
            min-width: 260px;
            height: 144px;
            gap: 12px;
          }
          .logo-img {
            max-height: 32px;
          }
          .logo-img--sm {
            max-height: 21px;
          }
          .logo-name {
            font-size: 12px;
          }
        }

        @media (min-width: 1024px) {
          .logo-tile {
            min-width: 320px;
            height: 176px;
            gap: 14px;
          }
          .logo-img {
            max-height: 40px;
          }
          .logo-img--sm {
            max-height: 26px;
          }
          .logo-name {
            font-size: 13px;
          }
        }

        @keyframes marquee-left {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }

        .animate-marquee-left {
          width: max-content;
          animation: marquee-left 36s linear infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .animate-marquee-left {
            animation: none !important;
          }
        }

        :global(.dark) .logo-img {
          filter: grayscale(1) invert(1);
        }
      `}</style>
    </section>
  )
}
